import { useRefreshProfile } from "@penn/web-common";

import { InternalSurfaceBand } from "@/components/admin/InternalSurfaceBand";
import { OpenAsCustomerLink } from "@/components/admin/OpenAsCustomerLink";
import { WorkForSelector } from "@/components/work-for/WorkForSelector";
import { PennText } from "@/components/ui";
import { useAppSelector } from "@/stores/hooks";

/**
 * Choosing the customer to act for, on its own page outside the admin console.
 * The console needs `canManageUsers`/`canManageMasterData`; this needs only
 * `canWorkForOthers`, so sales and engineering can reach it.
 */
export const WorkForPage = () => {
  const user = useAppSelector((state) => state.auth.user);
  const refreshProfile = useRefreshProfile();

  return (
    // Same IC canvas as DashboardLayout: navy page, white surfaces on it. The
    // band keeps its own slate so it still reads as the surface marker.
    <div className="flex flex-col min-h-screen bg-penn-900 dark:bg-gray-950">
      <InternalSurfaceBand position="static" showWorkFor={false} />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto w-full max-w-3xl">
          <PennText as="h1" size="xl" color="on-navy" font="semibold" className="mb-1">
            Work for a customer
          </PennText>
          <PennText as="p" size="sm" color="on-navy-secondary" className="mb-6">
            Pick the customer you are acting for. Everything you create afterwards
            is recorded against them, with you as the author.
          </PennText>

          {user?.email ? (
            <div className="rounded-xl bg-white p-5 shadow-soft dark:bg-gray-900">
              <WorkForSelector
                variant="inline"
                selfUserId={user.id}
                selfEmail={user.email}
                selfFirstName={user.firstName}
                selfLastName={user.lastName}
                selfCompanyName={user.companyName}
                enabled
                onApplied={() => void refreshProfile()}
                // Renders only once a registered customer is picked, so it is
                // the one next step rather than a disabled control.
                actions={<OpenAsCustomerLink variant="button" />}
              />
            </div>
          ) : (
            <PennText as="p" size="sm" color="on-navy-secondary">
              Loading your profile…
            </PennText>
          )}
        </div>
      </main>
    </div>
  );
};
